import { lazy, type ComponentType, type LazyExoticComponent } from "react";
import { matchRoutes } from "react-router-dom";
import type { RouteDef } from "./types";

/**
 * Boot-time chunk warming (docs/plans/2026-10-15-lms-load-speed.md §4 Phase 1).
 *
 * The gate chain (identity → roles → location) takes ~1s before Layout and the
 * route page mount. These helpers start the page's chunk download at app boot
 * so it overlaps that chain. They only warm the module cache: what renders, and
 * when, is still decided by the route tree and its gates.
 */

/** A React.lazy component that also exposes its import factory, so a caller can warm the chunk without rendering. */
export type PreloadableLazy<T extends ComponentType> = LazyExoticComponent<T> & {
  preload: () => Promise<unknown>;
};

/**
 * `React.lazy` plus `.preload()`. The browser's module map dedupes the dynamic
 * import, so a preload followed by the real render downloads the chunk once.
 */
export function lazyWithPreload<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
): PreloadableLazy<T> {
  const component = lazy(factory) as PreloadableLazy<T>;
  component.preload = factory;
  return component;
}

/**
 * The registry entry the router will render for `pathname`, ranked the way
 * React Router ranks routes (static segments beat params beat "*"). Null when
 * nothing matches.
 */
export function findBootRoute(registry: readonly RouteDef[], pathname: string): RouteDef | null {
  const routeObjects = registry.map((r) => ({ path: r.path }));
  const match = matchRoutes(routeObjects, pathname)?.[0];
  if (!match) return null;
  return registry[routeObjects.indexOf(match.route)] ?? null;
}

/**
 * Does the boot path render inside the authenticated app shell (Layout)?
 * Redirects count as shell paths: every registry redirect points into the app.
 * Public, kiosk and widget pages (layout "public"/"none", including the "*"
 * fallback) never mount Layout, so they must not download it.
 */
export function bootsIntoAppShell(route: RouteDef | null): boolean {
  if (!route) return false;
  return route.kind === "redirect" || route.layout === "app";
}

/**
 * Starts the boot route's page chunk download. A no-op for redirects and for
 * eagerly imported elements (no `preload`). A failed warm-up is left to the
 * real render: React.lazy re-imports and the existing chunk recovery
 * (`vite:preloadError` reload + LazyLoadErrorBoundary) surfaces it there.
 */
export function preloadRouteChunk(route: RouteDef | null): void {
  if (!route || route.kind !== "page") return;
  const { preload } = route.element as Partial<PreloadableLazy<ComponentType>>;
  if (typeof preload !== "function") return;
  preload().catch(() => undefined);
}
